"use client";

import { useActionState, useEffect, useId, useRef } from "react";
import { useFormStatus } from "react-dom";
import { cn } from "@/lib/cn";
import { submitEnquiry } from "./actions";
import {
  type EnquiryState,
  emptyEnquiry,
  INTENTS,
  isIntent,
} from "./enquiry-schema";

const fieldClass =
  "min-h-12 w-full border border-ink-900/20 bg-paper px-4 py-3 text-[0.9375rem] text-ink-900 " +
  "transition-colors duration-300 placeholder:text-ink-400 hover:border-ink-900/40 " +
  "focus:border-ink-900 focus:outline-none";

function Field({
  label,
  name,
  error,
  children,
  required,
  hint,
}: {
  label: string;
  name: string;
  error?: string;
  children: (props: {
    id: string;
    name: string;
    "aria-invalid"?: true;
    "aria-describedby"?: string;
  }) => React.ReactNode;
  required?: boolean;
  hint?: string;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div>
      <label
        htmlFor={id}
        className="t-eyebrow mb-2.5 block text-ink-500"
      >
        {label}
        {required ? (
          <span className="ml-1 text-sky-600" aria-hidden="true">
            *
          </span>
        ) : (
          <span className="ml-2 normal-case tracking-normal text-ink-400">
            (optional)
          </span>
        )}
      </label>

      {children({
        id,
        name,
        ...(error ? { "aria-invalid": true as const } : {}),
        ...(describedBy ? { "aria-describedby": describedBy } : {}),
      })}

      {hint ? (
        <p id={hintId} className="mt-2 text-[0.75rem] text-ink-400">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="mt-2 text-[0.8125rem] text-[#9b2c2c]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();

  return (
    <button
      type="submit"
      disabled={pending}
      className={cn(
        "inline-flex min-h-12 items-center justify-center gap-3 bg-ink-900 px-8 text-[0.8125rem] font-medium tracking-[0.08em] text-paper uppercase",
        "transition-colors duration-300 hover:bg-sky-500 hover:text-ink-950",
        "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-ink-900 disabled:hover:text-paper",
      )}
    >
      {pending ? "Sending…" : "Send enquiry"}
      {pending ? (
        <span
          className="h-3.5 w-3.5 animate-spin rounded-full border border-current border-t-transparent"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}

export function EnquiryForm({ intent }: { intent?: string }) {
  const initial: EnquiryState = {
    ...emptyEnquiry,
    values: {
      ...emptyEnquiry.values,
      intent: isIntent(intent) ? intent : emptyEnquiry.values.intent,
    },
  };

  const [state, action] = useActionState(submitEnquiry, initial);
  const statusRef = useRef<HTMLDivElement>(null);

  // Move the reader to the outcome rather than leaving them at the button.
  useEffect(() => {
    if (state.status !== "idle") statusRef.current?.focus();
  }, [state]);

  if (state.status === "success") {
    return (
      <div
        ref={statusRef}
        tabIndex={-1}
        role="status"
        className="border border-ink-900/15 bg-paper p-8 focus:outline-none md:p-10"
      >
        <p className="t-eyebrow text-sky-600">Enquiry received</p>
        <p className="mt-5 font-[family-name:var(--font-display)] text-[1.375rem] leading-snug text-ink-900">
          {state.message}
        </p>
        <p className="mt-4 text-[0.9375rem] leading-relaxed text-ink-600">
          If your matter is time-critical, please say so in a follow-up and we
          will prioritise it accordingly.
        </p>
      </div>
    );
  }

  return (
    <form action={action} noValidate className="space-y-7">
      {state.status === "error" && state.message ? (
        <div
          ref={statusRef}
          tabIndex={-1}
          role="alert"
          className="border-l-2 border-[#9b2c2c] bg-[#9b2c2c]/5 px-5 py-4 text-[0.875rem] text-[#7b2222] focus:outline-none"
        >
          {state.message}
        </div>
      ) : null}

      <div className="grid gap-7 sm:grid-cols-2">
        <Field label="Full name" name="name" error={state.errors.name} required>
          {(props) => (
            <input
              {...props}
              type="text"
              autoComplete="name"
              defaultValue={state.values.name}
              className={fieldClass}
            />
          )}
        </Field>

        <Field
          label="Organisation"
          name="organisation"
          error={state.errors.organisation}
          required
        >
          {(props) => (
            <input
              {...props}
              type="text"
              autoComplete="organization"
              defaultValue={state.values.organisation}
              className={fieldClass}
            />
          )}
        </Field>

        <Field label="Email" name="email" error={state.errors.email} required>
          {(props) => (
            <input
              {...props}
              type="email"
              inputMode="email"
              autoComplete="email"
              defaultValue={state.values.email}
              className={fieldClass}
            />
          )}
        </Field>

        <Field label="Phone" name="phone">
          {(props) => (
            <input
              {...props}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              defaultValue={state.values.phone}
              className={fieldClass}
            />
          )}
        </Field>
      </div>

      <Field label="How can we help" name="intent" required>
        {(props) => (
          <select
            {...props}
            defaultValue={state.values.intent}
            className={cn(fieldClass, "appearance-none pr-10")}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' fill='none' stroke='%232a4370' stroke-width='1.5'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E\")",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 1rem center",
            }}
          >
            {Object.entries(INTENTS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field
        label="Your enquiry"
        name="message"
        error={state.errors.message}
        required
        hint="A sentence or two on the organisation and what you need is plenty to start."
      >
        {(props) => (
          <textarea
            {...props}
            rows={6}
            defaultValue={state.values.message}
            className={cn(fieldClass, "resize-y")}
          />
        )}
      </Field>

      {/* Honeypot — hidden from people, tempting to bots. */}
      <div aria-hidden="true" className="absolute h-px w-px overflow-hidden opacity-0">
        <label htmlFor="company_website">Company website</label>
        <input
          id="company_website"
          name="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-4 border-t border-ink-900/12 pt-7 sm:flex-row sm:items-center sm:justify-between">
        <SubmitButton />
        <p className="max-w-[38ch] text-[0.75rem] leading-relaxed text-ink-500">
          We use your details only to respond to this enquiry.
        </p>
      </div>
    </form>
  );
}
